import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import StarRating from "../components/StarRating";
import api, { apiError } from "../lib/api";
import { useLang } from "../i18n";
import { countryOptions, flagEmoji, regionName, codeFromName } from "../lib/countries";
import { Sparkles, Plus, X, ArrowLeft, Loader2, Upload } from "lucide-react";

const TEXTURE = "https://images.pexels.com/photos/6445359/pexels-photo-6445359.jpeg";
const empty = {
  name: "", category: "Metal", genre: "", subgenre: "", country: "", country_code: "",
  flag: "", wikipedia_url: "", official_url: "", deezer_url: "",
  spotify_url: "", apple_music_url: "", youtube_music_url: "", bandcamp_url: "",
  logo_url: "", members: [], albums: [], notes: "", rating: 0,
};

export default function AddBand() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [newAlbum, setNewAlbum] = useState("");
  const [newMember, setNewMember] = useState({ name: "", instruments: "" });
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const skipNext = useRef(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    const q = form.name.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const tm = setTimeout(async () => {
      try {
        const { data } = await api.get("/enrich/suggest", { params: { q } });
        setSuggestions(data.results || []);
        setShowSug(true);
      } catch (e) { console.error("suggest failed", e); }
    }, 350);
    return () => clearTimeout(tm);
  }, [form.name]);

  const pickSuggestion = (s) => {
    skipNext.current = true;
    setShowSug(false);
    setSuggestions([]);
    set("name", s.name);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/logo", fd);
      set("logo_url", data.url);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const autofill = async () => {
    if (!form.name.trim()) return;
    setFetching(true); setError("");
    try {
      const { data } = await api.post("/enrich/preview", { name: form.name });
      setForm((f) => ({
        ...f,
        ...data,
        members: data.members?.length ? data.members : f.members,
        albums: f.albums,
        rating: f.rating,
      }));
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setFetching(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setError("");
    try {
      const { data } = await api.post("/bands", form);
      navigate(`/bands/${data.id}`);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const addAlbum = () => {
    if (!newAlbum.trim()) return;
    set("albums", [...form.albums, newAlbum.trim()]);
    setNewAlbum("");
  };
  const addMember = () => {
    if (!newMember.name.trim()) return;
    set("members", [...form.members, {
      name: newMember.name.trim(),
      instruments: newMember.instruments.split(",").map((s) => s.trim()).filter(Boolean),
    }]);
    setNewMember({ name: "", instruments: "" });
  };

  const inputCls = "w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none";
  const labelCls = "label-caps text-xs text-zinc-400";
  const countries = useMemo(() => countryOptions(lang), [lang]);
  const currentCode = (form.country_code || codeFromName(form.country, lang) || "").toUpperCase();
  const changeCountry = (code) => setForm((f) => ({
    ...f, country_code: code, country: code ? regionName(code, lang) : "", flag: flagEmoji(code),
  }));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 label-caps text-sm" data-testid="back-btn">
          <ArrowLeft size={16} /> {t("back")}
        </button>
        <h1 className="heading text-4xl font-bold text-white mb-2">{t("add_band")}</h1>

        {error && <div data-testid="addband-error" className="mb-4 text-sm text-red-400 border border-red-900 bg-red-950/40 px-3 py-2 rounded-sm">{error}</div>}

        <div className="relative bg-zinc-900 border border-zinc-800 rounded-sm p-5 mb-6">
          <img src={TEXTURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 rounded-sm pointer-events-none" />
          <div className="relative">
            <p className="label-caps text-xs text-primary mb-2">{t("autofill")}</p>
            <p className="text-sm text-zinc-400 mb-3">{t("autofill_hint")}</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1 min-w-0">
                <input data-testid="band-name-input" value={form.name}
                  onChange={(e) => { set("name", e.target.value); setShowSug(true); }}
                  onFocus={() => suggestions.length && setShowSug(true)}
                  placeholder={t("band_name")} className={inputCls} autoComplete="off" />
                {showSug && suggestions.length > 0 && (
                  <div data-testid="suggestions-dropdown" className="absolute z-20 left-0 right-0 mt-1 bg-zinc-950 border border-zinc-700 rounded-sm max-h-64 overflow-y-auto shadow-xl">
                    {suggestions.map((s, i) => (
                      <button key={i} type="button" data-testid={`suggestion-${i}`} onClick={() => pickSuggestion(s)}
                        className="w-full text-left px-3 py-2 hover:bg-zinc-800 flex items-center gap-2 border-b border-zinc-800/60 last:border-0">
                        {s.flag && <span className="text-base">{s.flag}</span>}
                        <span className="text-sm text-white truncate">{s.name}</span>
                        {s.disambiguation && <span className="text-xs text-zinc-500 truncate hidden sm:inline">— {s.disambiguation}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button data-testid="autofill-btn" onClick={autofill} disabled={fetching}
                className="flex items-center justify-center gap-2 whitespace-nowrap px-4 py-2.5 w-full sm:w-auto bg-primary text-primary-foreground label-caps rounded-sm font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-60">
                {fetching ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {fetching ? t("fetching") : t("fetch_info")}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-5 bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t("category")}</label>
              <select data-testid="category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                <option value="Metal">Metal</option>
                <option value="Rock">Rock</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>{t("rating")}</label>
              <div className="mt-2"><StarRating value={form.rating} onChange={(v) => set("rating", v)} testid="addband-rating" /></div>
            </div>
            <div>
              <label className={labelCls}>{t("genre")}</label>
              <input data-testid="genre-input" value={form.genre} onChange={(e) => set("genre", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t("subgenre")}</label>
              <input data-testid="subgenre-input" value={form.subgenre} onChange={(e) => set("subgenre", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t("country")}</label>
              <select data-testid="country-input" value={currentCode} onChange={(e) => changeCountry(e.target.value)} className={inputCls}>
                <option value="">—</option>
                {countries.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t("logo")} URL</label>
              <input data-testid="logo-input" value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="flex items-center gap-4">
            {form.logo_url ? (
              <img src={form.logo_url} alt="logo" className="h-28 w-28 object-cover rounded-sm border border-zinc-800" />
            ) : (
              <div className="h-28 w-28 flex items-center justify-center rounded-sm border border-dashed border-zinc-700 text-zinc-600 text-xs text-center px-2">{t("no_auto_logo")}</div>
            )}
            <label data-testid="upload-logo-btn" className="flex items-center gap-2 px-4 py-2 border border-zinc-700 rounded-sm text-zinc-200 hover:border-primary hover:text-primary cursor-pointer label-caps text-sm">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? t("uploading") : t("upload_logo")}
              <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
            </label>
          </div>

          <div className="grid gap-3">
            <div><label className={labelCls}>{t("wikipedia")}</label><input data-testid="wiki-input" value={form.wikipedia_url} onChange={(e) => set("wikipedia_url", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>{t("official_site")}</label><input data-testid="official-input" value={form.official_url} onChange={(e) => set("official_url", e.target.value)} className={inputCls} /></div>
            <p className={labelCls + " pt-2"}>{t("streaming")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={labelCls}>{t("deezer")}</label><input data-testid="deezer-input" value={form.deezer_url} onChange={(e) => set("deezer_url", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>{t("spotify")}</label><input data-testid="spotify-input" value={form.spotify_url} onChange={(e) => set("spotify_url", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>{t("apple_music")}</label><input data-testid="apple-input" value={form.apple_music_url} onChange={(e) => set("apple_music_url", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>{t("youtube_music")}</label><input data-testid="ytm-input" value={form.youtube_music_url} onChange={(e) => set("youtube_music_url", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>{t("bandcamp")}</label><input data-testid="bandcamp-input" value={form.bandcamp_url} onChange={(e) => set("bandcamp_url", e.target.value)} className={inputCls} /></div>
            </div>
          </div>

          <div>
            <label className={labelCls}>{t("members")}</label>
            <div className="space-y-2 mt-2">
              {form.members.map((m, i) => (
                <div key={i} className="flex items-center justify-between bg-black border border-zinc-800 rounded-sm px-3 py-2">
                  <span className="text-sm text-white">{m.name} <span className="text-zinc-500">— {m.instruments.join(", ")}</span></span>
                  <button onClick={() => set("members", form.members.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400"><X size={16} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input data-testid="member-name" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} placeholder={t("name")} className={inputCls} />
                <input data-testid="member-instruments" value={newMember.instruments} onChange={(e) => setNewMember({ ...newMember, instruments: e.target.value })} placeholder={t("instruments")} className={inputCls} />
                <button data-testid="add-member-btn" onClick={addMember} className="px-3 border border-zinc-700 rounded-sm text-primary hover:bg-zinc-800"><Plus size={18} /></button>
              </div>
            </div>
          </div>

          <div>
            <label className={labelCls}>{t("albums")}</label>
            <div className="space-y-2 mt-2">
              {form.albums.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-black border border-zinc-800 rounded-sm px-3 py-2">
                  <span className="text-sm text-white">{a}</span>
                  <button onClick={() => set("albums", form.albums.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400"><X size={16} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input data-testid="album-input" value={newAlbum} onChange={(e) => setNewAlbum(e.target.value)} placeholder={t("add_album")} className={inputCls} />
                <button data-testid="add-album-btn" onClick={addAlbum} className="px-3 border border-zinc-700 rounded-sm text-primary hover:bg-zinc-800"><Plus size={18} /></button>
              </div>
            </div>
          </div>

          <div>
            <label className={labelCls}>{t("notes")}</label>
            <textarea data-testid="notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className={inputCls} />
          </div>

          <div className="flex gap-3 pt-2">
            <button data-testid="save-band-btn" onClick={save} disabled={saving || !form.name.trim()}
              className="flex-1 bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-60">
              {saving ? t("saving") : t("save")}
            </button>
            <button onClick={() => navigate("/dashboard")} className="px-6 border border-zinc-700 text-zinc-300 label-caps rounded-sm hover:bg-zinc-800">{t("cancel")}</button>
          </div>
        </div>
      </main>
    </div>
  );
}
